/**
 * ─────────────────────────────────────────────────────────────
 * DESIGN TOKENS — « la vitrine d’herboriste du 41 rue Thiers ».
 *
 * Every value here comes from a real thing, sampled on the salon’s own photos
 * (see docs/site-design.md → Palette). The MUI theme derives from `siteConfig.brand`
 * for primary/secondary; the rest of the palette lives here so no section ever
 * hard-codes a hex code of its own.
 * ─────────────────────────────────────────────────────────────
 */

export const colors = {
  /** Le crème plâtré du panneau d’enseigne et de la feuille de tarifs. */
  cream: '#F6EFE4',
  /** Le même crème, un demi-ton plus bas. */
  creamDeep: '#EFE7DA',
  /** Un charbon chaud tiré vers le vert de l’enseigne — jamais du noir pur. */
  ink: '#252B22',
  /** Le lettrage « L’Autre Moi » du logo. */
  green: '#37672F',
  /** Le vinyle botanique et le « naturel bio » de la vitrine. Décor uniquement. */
  sage: '#A8C3B0',
  /** Le médaillon ovale du logo. Surface et filets structurants — jamais du texte. */
  olive: '#C9C07C',
  /**
   * **L’argile.** L’accent du site et sa seule bande chaude. « Argile » est l’un
   * des quatre mots que Google extrait de leurs avis clients, à côté de
   * confiance et prix — ils font vraiment des soins à l’argile, la couleur ne
   * sort pas de nulle part.
   *
   * **Saturation calée sur celle du vert enseigne** (42 % contre 37 %) : la
   * première version, `#A0492C` à 57 %, criait à côté de lui. Une terre cuite,
   * pas une brique neuve. Assez sombre pour porter du texte crème (5,7:1) et
   * pour s’écrire sur le crème (5,7:1), des deux côtés du même ton.
   */
  clay: '#8D4D3A',
  /**
   * Le lettrage « Coiffure » de l’enseigne. **Retiré du site** : l’argile a
   * repris ses deux emplois, deux rouges voisins se seraient contrariés.
   * Conservé ici parce que c’est une couleur relevée sur leur devanture, si
   * jamais on veut y revenir.
   */
  bordeaux: '#6B1F28',
  /** Les appliques allumées vues de la rue. Décoratif uniquement, jamais du texte. */
  amber: '#E0A340',
  /**
   * La même ambre, assombrie jusqu’à passer 3:1 sur le crème. Existe pour une
   * seule chose : les étoiles de la note Google, qui portent du sens et doivent
   * donc tenir le contraste des graphiques informatifs (WCAG 1.4.11).
   */
  amberDeep: '#B07615',
  /** L’aluminium des châssis. */
  frame: '#9FAAAC',
  /** Le crème assombri, pour les filets d’un pixel. */
  hairline: '#DCD3C4',
} as const;

/**
 * La signature de forme du site : toutes les images sont cintrées en arc, comme
 * la baie de la devanture. Une seule règle, appliquée à chaque image, sans exception.
 */
export const ARCH = '999px 999px 4px 4px / 180px 180px 4px 4px';

/** Largeur de contenu et colonne de texte (62 caractères). */
export const CONTENT_WIDTH = 1080;
export const TEXT_MEASURE = '62ch';

/** Rythme : 96 px entre sections (64 px en mobile), 32 px à l’intérieur. */
export const SECTION_Y = { xs: 8, md: 12 } as const; // 64 px / 96 px
export const BLOCK_GAP = 4; // 32 px

/** Entrée : fondu + montée de 16 px, 420 ms — une seule et même entrée partout. */
export const REVEAL = { distance: 16, duration: 0.42 } as const;
